import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { translations } from '../translations';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('crp_token'));
  const [isLoading, setIsLoading] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem('crp_theme') || 'light');
  const [language, setLanguage] = useState(() => localStorage.getItem('crp_lang') || 'en');

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    localStorage.setItem('crp_theme', theme);
  }, [theme]);

  // Load user on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('crp_token');
    if (storedToken) {
      fetchMe(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchMe = async (t) => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      setUser(res.data);
      setToken(t);
    } catch {
      localStorage.removeItem('crp_token');
      setToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = useCallback((newToken, userData) => {
    localStorage.setItem('crp_token', newToken);
    setToken(newToken);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('crp_token');
    setToken(null);
    setUser(null);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('crp_theme', next);
      return next;
    });
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => {
      const next = prev === 'en' ? 'ta' : 'en';
      localStorage.setItem('crp_lang', next);
      return next;
    });
  }, []);

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  }, [language]);

  const apiCall = useCallback(async (method, path, data = null) => {
    const config = {
      method,
      url: `${API}${path}`,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    };
    if (data) config.data = data;
    const res = await axios(config);
    return res.data;
  }, [token]);

  return (
    <AppContext.Provider value={{
      user, token, isLoading,
      theme, language,
      login, logout,
      toggleTheme, toggleLanguage,
      t, apiCall, API
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
